import { useEffect, useRef } from 'react'
import useStableCallback from './useStableCallback'

const START_AFTER_MS = 45000
const PAUSE_MS = 5000
const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

export const usePollAfter = (pollFn: () => Promise<void>, syncStartTime: Date | null) => {
  const runningRef = useRef(syncStartTime !== null)
  runningRef.current = syncStartTime !== null
  const startTimeRef = useRef<Date | null>(syncStartTime)
  startTimeRef.current = syncStartTime
  const loopRunningRef = useRef(false)
  const start = useStableCallback(async () => {
    if (loopRunningRef.current) {
      // prevent multiple loops
      return
    }
    loopRunningRef.current = true
    try {
      while (runningRef.current) {
        if (!startTimeRef.current) {
          break
        }
        const eligibleAtMs = startTimeRef.current.getTime() + START_AFTER_MS
        const startDelayMs = Math.max(0, eligibleAtMs - Date.now())
        if (startDelayMs > 0) {
          await sleep(startDelayMs)
          if (!runningRef.current) {
            break
          }
        }
        try {
          await pollFn()
        } catch {
          // swallow by default
        }
        if (!runningRef.current) {
          break
        }
        await sleep(PAUSE_MS)
      }
    } finally {
      loopRunningRef.current = false
    }
  })
  useEffect(() => {
    if (syncStartTime === null) {
      return
    }
    void start()
    return () => {
      runningRef.current = false
    }
  }, [syncStartTime, start])
}
